import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input, Label } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useTheme } from "../contexts/ThemeContext";
import { Sun, Moon, Lock, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import { useNavigate } from "react-router-dom";

export default function SettingsPage() {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <Toaster richColors position="top-right" />

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Preferences, security, and workspace configuration.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>Switch between light and dark modes.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <button
              data-testid="settings-theme-light"
              onClick={() => setTheme("light")}
              className={`flex-1 border rounded-md p-4 text-left transition-colors ${
                theme === "light"
                  ? "border-primary bg-primary-subtle dark:bg-primary/15"
                  : "border-border hover:bg-muted"
              }`}
            >
              <Sun className="w-5 h-5 mb-2 text-primary" />
              <div className="text-sm font-semibold">Light</div>
              <div className="text-xs text-muted-foreground">Enterprise default</div>
            </button>
            <button
              data-testid="settings-theme-dark"
              onClick={() => setTheme("dark")}
              className={`flex-1 border rounded-md p-4 text-left transition-colors ${
                theme === "dark"
                  ? "border-primary bg-primary-subtle dark:bg-primary/15"
                  : "border-border hover:bg-muted"
              }`}
            >
              <Moon className="w-5 h-5 mb-2 text-primary" />
              <div className="text-sm font-semibold">Dark</div>
              <div className="text-xs text-muted-foreground">Low-light workstations</div>
            </button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Security</CardTitle>
          <CardDescription>Change your password. Choose a strong, unique password.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Current password</Label>
              <Input
                data-testid="change-password-current"
                type="password"
                className="mt-1.5"
                value={pw.current_password}
                onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>New password</Label>
              <Input
                data-testid="change-password-new"
                type="password"
                className="mt-1.5"
                value={pw.new_password}
                onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input
                data-testid="change-password-confirm"
                type="password"
                className="mt-1.5"
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                required
              />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" data-testid="change-password-submit" disabled={saving}>
                <Lock className="w-4 h-4" />
                {saving ? "Updating…" : "Update password"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
